// Setup Module
import ForeplayAPI from '@/api/foreplayServer'
import { SETUP_STEPS } from '@/utils/setup/setupSteps'
import { isStepDone, isStepSkipped, isStepLocked, isProductLocked, computeProgress } from '@/utils/setup/setupProgress'
import { getLockedProductMap } from '@/utils/productAccess'
import { SUBSCRIPTION_STATES } from '@/utils/stripe/productMeta'

// In-flight progress request, shared so concurrent callers (sidebar + Setup page) await the same fetch
let progressRequest = null
let queuedRequest = null

export default {
  namespaced: true,
  state: {
    // Server-computed completion map: { [stepId]: { done } }
    completions: {},
    // Steps the client knows are done before the server confirms them (e.g. a
    // guided walkthrough just finished). Dropped once the server agrees.
    optimisticCompletions: {},
    // Skips / un-skips the user just made: { [stepId]: boolean }. Dropped once the server agrees.
    optimisticSkips: {},
    loading: false,
    loaded: false
  },
  getters: {
    getSetupCompletions: state => {
      // Optimistic ticks win until the server confirms them (SET_COMPLETIONS drops them then)
      const completions = { ...state.completions, ...state.optimisticCompletions }
      Object.keys(state.optimisticSkips).forEach(stepId => {
        completions[stepId] = { ...completions[stepId], skipped: state.optimisticSkips[stepId] }
      })
      return completions
    },
    getSetupLockedProducts: (state, getters, rootState, rootGetters) => {
      const subscription = rootGetters['AuthModule/getUserSubscription']
      const isLegacy = rootGetters['AuthModule/getIsLegacy']
      const hasApiAccess = rootGetters['AuthModule/getHasApiAccess']
      const lockedProducts = getLockedProductMap({
        subscription,
        isLegacy,
        lensLimit: rootGetters['AuthModule/getLensLimit'],
        spyderLimit: rootGetters['AuthModule/getSpyderLimit'],
        guestStatus: rootGetters['AuthModule/getGuestStatus'],
        hasApiAccess,
        paymentPastDue: subscription?.status === SUBSCRIPTION_STATES.PAST_DUE
      })
      // API access can be unlocked for free from the API paywall, so the MCP
      // step stays available. Only legacy plans without access must upgrade.
      return { ...lockedProducts, api: isLegacy && !hasApiAccess }
    },
    // Steps with done / locked / productLocked state applied. A step is
    // product-locked when the plan lacks its product or the server flags it
    // upgradeRequired. Done steps are never product-locked (work done before a
    // downgrade still counts, matching the server).
    getSetupSteps: (state, getters) => {
      const completions = getters.getSetupCompletions
      const lockedProducts = getters.getSetupLockedProducts
      const isUnavailable = step => !isStepDone(completions, step.id) &&
        (isProductLocked(step, lockedProducts) || !!state.completions[step.id]?.upgradeRequired)
      const unavailableStepIds = SETUP_STEPS.filter(isUnavailable).map(step => step.id)

      return SETUP_STEPS.map(step => {
        const done = isStepDone(completions, step.id)
        const productLocked = unavailableStepIds.includes(step.id)
        return {
          ...step,
          done,
          // A plan-locked step shows its upgrade state even if it was skipped
          skipped: !done && !productLocked && isStepSkipped(completions, step.id),
          productLocked,
          locked: !done && isStepLocked(completions, step, unavailableStepIds)
        }
      })
    },
    // Product-locked steps don't count toward progress
    getSetupProgress: (state, getters) => {
      const availableSteps = getters.getSetupSteps.filter(step => !step.productLocked)
      return computeProgress(getters.getSetupCompletions, availableSteps)
    },
    getSetupPercent: (state, getters) => getters.getSetupProgress.percent,
    getSetupComplete: (state, getters) => getters.getSetupProgress.percent >= 100,
    getSetupProgressLoaded: state => state.loaded
  },
  mutations: {
    SET_COMPLETIONS (state, completions) {
      state.completions = completions || {}

      // Drop optimistic entries the server now confirms
      const remaining = {}
      Object.keys(state.optimisticCompletions).forEach(stepId => {
        if (!isStepDone(state.completions, stepId)) remaining[stepId] = state.optimisticCompletions[stepId]
      })
      state.optimisticCompletions = remaining

      // Drop optimistic skips the server now reflects (or that a real completion overrides)
      const pendingSkips = {}
      Object.keys(state.optimisticSkips).forEach(stepId => {
        const skipped = state.optimisticSkips[stepId]
        if (isStepDone(state.completions, stepId) || isStepSkipped(state.completions, stepId) === skipped) return
        pendingSkips[stepId] = skipped
      })
      state.optimisticSkips = pendingSkips
    },
    SET_OPTIMISTIC_COMPLETION (state, stepId) {
      state.optimisticCompletions = { ...state.optimisticCompletions, [stepId]: { done: true } }
    },
    SET_OPTIMISTIC_SKIP (state, { stepId, skipped }) {
      state.optimisticSkips = { ...state.optimisticSkips, [stepId]: skipped }
    },
    CLEAR_OPTIMISTIC_SKIP (state, stepId) {
      const { [stepId]: cleared, ...remaining } = state.optimisticSkips
      state.optimisticSkips = remaining
    },
    SET_LOADING (state, loading) {
      state.loading = loading
    },
    SET_LOADED (state, loaded) {
      state.loaded = loaded
    }
  },
  actions: {
    // Fetch Setup Progress (cached after the first load unless forced)
    async fetchSetupProgress ({ state, commit, dispatch }, { force = false } = {}) {
      if (progressRequest) {
        if (!force) return progressRequest
        if (!queuedRequest) {
          queuedRequest = progressRequest.then(() => {
            queuedRequest = null
            return dispatch('fetchSetupProgress', { force: true })
          })
        }
        return queuedRequest
      }
      if (state.loaded && !force) return

      commit('SET_LOADING', true)
      progressRequest = (async () => {
        try {
          const { steps } = await ForeplayAPI.Setup.getProgress() || {}
          commit('SET_COMPLETIONS', steps)
          commit('SET_LOADED', true)
        } catch (error) {
          console.error('Error fetching setup progress', error)
        } finally {
          commit('SET_LOADING', false)
          progressRequest = null
        }
      })()
      return progressRequest
    },
    // Tick a step off immediately, before the server reflects it
    markStepCompleted ({ commit }, stepId) {
      if (!stepId) return
      commit('SET_OPTIMISTIC_COMPLETION', stepId)
    },
    async stampStep ({ commit, dispatch }, stepId) {
      if (!stepId) return
      commit('SET_OPTIMISTIC_COMPLETION', stepId)
      try {
        await ForeplayAPI.Setup.stamp(stepId)
      } catch (error) {
        console.error('Error stamping setup step', stepId, error)
      }
      dispatch('fetchSetupProgress', { force: true })
    },
    // Skip a step (or undo with skipped = false). Flips the row immediately and
    // rolls back if the server rejects it; callers handle the error.
    async setStepSkipped ({ commit, dispatch }, { stepId, skipped = true }) {
      if (!stepId) return
      commit('SET_OPTIMISTIC_SKIP', { stepId, skipped })
      try {
        await ForeplayAPI.Setup.skip(stepId, skipped)
      } catch (error) {
        commit('CLEAR_OPTIMISTIC_SKIP', stepId)
        throw error
      }
      dispatch('fetchSetupProgress', { force: true })
    }
  }
}
